import { useEffect, useState } from "react";
import { Share, PlusSquare, Bell, BellRing, Smartphone, Loader2, Copy, Settings, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { enablePush } from "@/lib/onesignal";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
export const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isStandalone = () => window.navigator.standalone === true || window.matchMedia("(display-mode: standalone)").matches;
const notSafari = /CriOS|FxiOS|EdgiOS|OPiOS|GSA|Instagram|FBAN|FBAV|WhatsApp/i.test(ua);
const permission = () => ("Notification" in window ? Notification.permission : "unsupported");

function Shell({ step, children }) {
  return (
    <div data-testid="ios-gate" className="max-w-md mx-auto min-h-screen bg-[#08080C] border-x border-amber-500/10 px-6 py-10 flex flex-col">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center"><Sparkles className="w-4 h-4 text-slate-950" /></div>
        <span className="gold-text font-bold text-xl">Avenue Benda</span>
      </div>
      <div className="flex gap-2 mt-8">
        {[1, 2].map((s) => <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? "bg-amber-500" : "bg-slate-700"}`} />)}
      </div>
      <p className="text-[11px] font-mono uppercase tracking-widest text-amber-400/70 mt-3" data-testid="ios-gate-step">Étape {step} sur 2 — obligatoire</p>
      {children}
      <p className="text-xs text-slate-500 mt-auto pt-8">Indispensable pour obtenir le suivi complet du livreur et être averti de son arrivée.</p>
    </div>
  );
}

function Instruction({ n, icon: Icon, children }) {
  return (
    <div className="flex items-start gap-3 glass rounded-2xl p-4 border border-amber-500/15">
      <span className="w-7 h-7 rounded-full btn-gold flex items-center justify-center text-sm font-bold shrink-0">{n}</span>
      <p className="text-sm text-slate-200 flex-1 leading-relaxed">{children}</p>
      <Icon className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
    </div>
  );
}

function AddToHomeStep() {
  const copy = async () => {
    try { await navigator.clipboard.writeText(window.location.origin); toast.success("Lien copié — collez-le dans Safari"); }
    catch (e) { toast.error("Copiez l'adresse manuellement : " + window.location.origin); }
  };
  return (
    <Shell step={1}>
      <h1 className="text-3xl font-bold text-slate-100 mt-4 leading-tight">Ajoutez Avenue Benda à votre <span className="gold-text">écran d'accueil</span></h1>
      <p className="text-sm text-slate-400 mt-2">Sur iPhone, c'est la seule façon de recevoir l'alerte « Votre livreur est arrivé ».</p>
      {notSafari && (
        <div data-testid="ios-gate-open-safari" className="mt-5 rounded-2xl border border-rose-500/40 bg-rose-500/10 p-4 text-sm text-rose-100">
          Vous n'êtes pas dans Safari. Ouvrez ce site dans <b>Safari</b> pour continuer.
          <Button data-testid="ios-gate-copy-link" onClick={copy} className="w-full btn-gold rounded-xl mt-3"><Copy className="w-4 h-4 mr-2" /> Copier le lien</Button>
        </div>
      )}
      <div className="space-y-3 mt-6">
        <Instruction n={1} icon={Share}>Touchez le bouton <b className="text-amber-300">Partager</b> en bas de Safari (carré avec une flèche vers le haut).</Instruction>
        <Instruction n={2} icon={PlusSquare}>Faites défiler puis touchez <b className="text-amber-300">Sur l'écran d'accueil</b>, puis <b className="text-amber-300">Ajouter</b>.</Instruction>
        <Instruction n={3} icon={Smartphone}>Fermez Safari et ouvrez <b className="text-amber-300">Avenue Benda</b> depuis sa nouvelle icône, puis reconnectez-vous.</Instruction>
      </div>
      <div className="mt-8 flex flex-col items-center text-amber-400 animate-bounce" data-testid="ios-gate-share-hint">
        <span className="text-xs font-mono">Bouton Partager ci-dessous</span>
        <Share className="w-7 h-7 mt-1" />
      </div>
    </Shell>
  );
}

function NotificationsStep({ onDone }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [perm, setPerm] = useState(permission());
  const enable = async () => {
    setBusy(true);
    try {
      const ok = await enablePush(user.role, user.id);
      setPerm(permission());
      if (ok) { toast.success("Notifications activées"); onDone(); }
      else toast.error("Autorisation refusée");
    } catch (e) { toast.error("Impossible d'activer les notifications"); setPerm(permission()); }
    finally { setBusy(false); }
  };
  return (
    <Shell step={2}>
      <h1 className="text-3xl font-bold text-slate-100 mt-4 leading-tight">Activez les <span className="gold-text">notifications</span></h1>
      <p className="text-sm text-slate-400 mt-2">Vous serez prévenu quand votre livreur est en route et quand il est arrivé, même écran verrouillé.</p>
      {perm === "unsupported" ? (
        <div data-testid="ios-gate-update-ios" className="mt-6 rounded-2xl border border-rose-500/40 bg-rose-500/10 p-4 text-sm text-rose-100">
          Votre iPhone doit être à jour (iOS 16.4 minimum). Allez dans Réglages › Général › Mise à jour logicielle.
        </div>
      ) : perm === "denied" ? (
        <div data-testid="ios-gate-denied" className="mt-6 space-y-3">
          <Instruction n={1} icon={Settings}>Ouvrez <b className="text-amber-300">Réglages › Notifications › Avenue Benda</b>.</Instruction>
          <Instruction n={2} icon={BellRing}>Activez <b className="text-amber-300">Autoriser les notifications</b>, puis revenez ici.</Instruction>
          <Button data-testid="ios-gate-recheck" onClick={() => window.location.reload()} className="w-full btn-gold rounded-xl py-6 text-base">J'ai activé, continuer</Button>
        </div>
      ) : (
        <>
          <div className="mt-8 flex justify-center"><div className="w-24 h-24 rounded-full bg-amber-500/15 border border-amber-500/50 flex items-center justify-center animate-pulse"><Bell className="w-10 h-10 text-amber-400" /></div></div>
          <Button data-testid="ios-gate-enable-push" disabled={busy} onClick={enable} className="w-full btn-gold rounded-xl py-7 text-base mt-8">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><BellRing className="w-5 h-5 mr-2" /> Activer les notifications</>}
          </Button>
          <p className="text-xs text-slate-400 text-center mt-3">Puis choisissez <b className="text-amber-300">Autoriser</b> dans la fenêtre iOS.</p>
        </>
      )}
    </Shell>
  );
}

export const IosGate = ({ children }) => {
  const [granted, setGranted] = useState(permission() === "granted");
  const standalone = isIOS && isStandalone();
  useEffect(() => {
    if (!standalone || granted) return;
    const onFocus = () => { if (permission() === "granted") setGranted(true); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [standalone, granted]);
  if (!isIOS) return children;
  if (!standalone) return <AddToHomeStep />;
  if (!granted) return <NotificationsStep onDone={() => setGranted(true)} />;
  return children;
};
